import {
    Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, IconButton, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Switch, Text, Textarea, Checkbox, Radio, RadioGroup, Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CustomTable, CustomToast, Dropdown, FormInput } from "../../../../components";
import { Column } from "../../../../components/customTable/CustomTable";
import AddIcon from "../../../../assets/Images/Plus-icon.svg"
import { Controller, useForm } from "react-hook-form";
import { useFetch } from "../../../../hooks/useFetch";
import { yupResolver } from "@hookform/resolvers/yup";
import { showDate, showTime } from "../../../../utilities/helper";
import { EditIcon, CopyIcon } from "@chakra-ui/icons";
import MyDiv from "./meetingForm.style";
import { refreshHistory } from "../../../../components/History/modal";
import { defaultValues, formatMeetingDate, IMeeting, IMeetingForm, isValidEmail, isValidUrl, meetingDurationOptions, meetingFilters, meetingSchema, statusFilters, StatusType, statusTypeOptions } from "./model";

interface Props {
    leadId: number;
    viewType?: "table" | "card";
    onMeetingSaved?: (leadId: number) => void;
    pocEmail?: string;
}


const MeetingFormTab = ({ leadId, viewType = "table", onMeetingSaved, pocEmail: propPocEmail }: Props) => {
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const { addToast } = CustomToast();
    const [meetings, setMeetings] = useState<IMeeting[]>([]);
    const { fetchApi, loading } = useFetch(addToast);
    const [filterLoading, setFilterLoading] = useState(false);
    const [meetingFilter, setMeetingFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [emailInput, setEmailInput] = useState("");
    const [emailList, setEmailList] = useState<string[]>(propPocEmail ? [propPocEmail.toLowerCase()] : []);
    const [emailError, setEmailError] = useState("");
    const [originalStatus, setOriginalStatus] = useState<StatusType | null>(null);
    const [pocEmail, setPocEmail] = useState(propPocEmail || "");
    const [isReadOnlyMeeting, setIsReadOnlyMeeting] = useState(false);

    const [includeSignature, setIncludeSignature] = useState(true);
    const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
    const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

    const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
    const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string>("");
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);
    const [locations, setLocations] = useState<{ id: number; name: string; locationName: string }[]>([]);

    const loadLocations = async () => {
        try {
            const res = await fetchApi("Dropdown/getLocation", "GET");
            const rawList = Array.isArray(res)
                ? res
                : Array.isArray((res as any)?.data)
                ? (res as any).data
                : Array.isArray((res as any)?.locations)
                ? (res as any).locations
                : Array.isArray((res as any)?.result)
                ? (res as any).result
                : [];

            if (rawList && rawList.length > 0) {
                const formatted = rawList.map((x: any) => {
                    const idVal = x.id ?? x.Id ?? x.locationId ?? x.LocationId ?? x.value;
                    const nameVal = x.locationName ?? x.LocationName ?? x.name ?? x.Name ?? x.text ?? x.title ?? "";
                    return {
                        id: Number(idVal),
                        name: String(nameVal),
                        locationName: String(nameVal),
                        locationId: Number(idVal),
                    };
                });
                setLocations(formatted);
            }
        } catch (e) {
            console.error("Error loading locations:", e);
        }
    };

    useEffect(() => {
        loadLocations();
    }, []);

    useEffect(() => {
        const fetchSignature = async () => {
            if (includeSignature && !userSignature && !loadingSignature) {
                setLoadingSignature(true);
                try {
                    const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
                    if (res) {
                        setUserSignature({
                            shortSignature: res.shortSignature || "",
                            fullSignature: res.fullSignature || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching signature:", error);
                } finally {
                    setLoadingSignature(false);
                }
            }
        };
        fetchSignature();
    }, [includeSignature, userSignature, loadingSignature]);

    useEffect(() => {
        const fetchDisclaimer = async () => {
            if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
                setLoadingDisclaimer(true);
                try {
                    const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
                    if (res) {
                        setWebsiteDisclaimer(res.disclaimer || "");
                    }
                } catch (error) {
                    console.error("Error fetching disclaimer:", error);
                } finally {
                    setLoadingDisclaimer(false);
                }
            }
        };
        fetchDisclaimer();
    }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);

    const { control, watch, setValue, handleSubmit, reset, register, formState: { errors }, } = useForm<IMeetingForm>({
        resolver: yupResolver(meetingSchema),
        defaultValues,
    });

    const handleCopyLink = (link: string) => {
        if (!link) return;
        navigator.clipboard.writeText(link);
        addToast({ message: "Meeting link copied successfully!", status: "success", });
    };

    const isMeetingLinkActive = (row: IMeeting) => {
        if (row.meetingStatusType !== 1 && row.meetingStatusType !== 4) {
            return false;
        }
        if (!row.meeting_Link || !isValidUrl(row.meeting_Link)) return false;
        const now = new Date();
        if (row.isAllDay) {
            const todayStr = now.toLocaleDateString("en-CA");
            const meetingDateStr = row.meetingDate ? row.meetingDate.split("T")[0] : "";
            return todayStr === meetingDateStr;
        }
        if (!row.startDateTime || !row.endDateTime) return false;
        const start = new Date(row.startDateTime.endsWith("Z") ? row.startDateTime : `${row.startDateTime}Z`);
        const end = new Date(row.endDateTime.endsWith("Z") ? row.endDateTime : `${row.endDateTime}Z`);
        return now >= start && now <= end;
    };

    useEffect(() => {
        if (propPocEmail) {
            const email = propPocEmail.toLowerCase();
            setPocEmail(email);
            if (editId === null) {
                setEmailList(prev => prev.includes(email) ? prev : [email]);
            }
        } else if (leadId) {
            const getPocDetails = async () => {
                const res = await fetchApi(`Lead/GetLeadCompanyPocByLeadId/${leadId}`, "GET");
                if (res?.status && res.data?.pocEmail) {
                    const email = res.data.pocEmail.toLowerCase();
                    setPocEmail(email);
                    if (editId === null) {
                        setEmailList(prev => prev.includes(email) ? prev : [email]);
                    }
                }
            };
            getPocDetails();
        }
    }, [leadId, propPocEmail, editId]);

    const isAllDay = watch("isAllDay");
    const selectedMeetingLinkType = watch("meetingLinkType");
    const meetingLinkTypeOptions = isAllDay
        ? [
            { id: "usingLink", name: "Using Link" },
            { id: "locationVisit", name: "Location Visit" },
        ]
        : [
            { id: "googleLink", name: "Google Link" },
            { id: "usingLink", name: "Using Link" },
            { id: "locationVisit", name: "Location Visit" },
        ];


    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;
            if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
            if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
            setEmailList(prev => [...prev, trimmed]);
            setEmailInput("");
            setEmailError("");
        }
    };

    const handleEmailBlur = () => {
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
        if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
        setEmailList(prev => [...prev, trimmed]);
        setEmailInput("");
        setEmailError("");
    };

    const removeEmail = (email: string) =>
        setEmailList(prev => prev.filter(e => e !== email));


    const startTime = watch("startDateTime");
    const today = new Date().toISOString().split("T")[0];

    const getStatusOptions = (currentStatus?: StatusType) => {
        switch (currentStatus) {
            case StatusType.Rescheduled:
                return statusTypeOptions.filter(
                    x => x.id !== StatusType.Scheduled

                );

            case StatusType.Done:
                return [{ id: StatusType.Done, name: "Done" }];

            case StatusType.Cancelled:
                return [{ id: StatusType.Cancelled, name: "Cancelled" }];

            default:
                return statusTypeOptions;
        }
    };

    const loadMeetings = async () => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setFilterLoading(true);
        const res = await fetchApi(`LeadRequirement/LeadMeeting?leadId=${leadId}&filter=${meetingFilter}&status=${statusFilter}&timeZone=${encodeURIComponent(timezone)}`, "GET");
        if (Array.isArray(res?.data)) {
            setMeetings(res.data);
            setFilterLoading(false);
        } else {
            setMeetings([]);
        }
    };

    useEffect(() => {
        setMeetings([]);
        if (leadId) {
            loadMeetings();
        }
    }, [leadId, meetingFilter, statusFilter]);


    const onSubmit = async (data: IMeetingForm) => {
        // if (editId === null && !data.isAllDay && data.startDateTime) {
        //     const selectedStartDateTime = new Date(`${data.meetingDate}T${data.startDateTime}`);
        //     if (selectedStartDateTime.getTime() <= Date.now()) {
        //         addToast({ message: "Meeting start time must be greater than current time.", status: "warning", });
        //         return;
        //     }
        // }

        const isReschedule = editId !== null && data.meetingStatusType === StatusType.Rescheduled;

        const shouldValidateTime = !data.isAllDay && data.startDateTime && (editId === null || isReschedule);

        if (shouldValidateTime) {
            // User's local datetime
            const selectedStartDateTime = new Date(
                `${data.meetingDate}T${data.startDateTime}`
            );

            const now = new Date();

            if (selectedStartDateTime <= now) {
                addToast({
                    message: "Rescheduled meeting must be scheduled for a future date and time.",
                    status: "warning",
                });
                return;
            }
        }
        let finalEmails = [...emailList];
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (trimmed) {
            if (isValidEmail(trimmed)) {
                if (!finalEmails.includes(trimmed)) {
                    finalEmails.push(trimmed);
                    setEmailList(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
                    setEmailInput("");
                    setEmailError("");
                }
            } else {
                setEmailError("Invalid email address.");
                return;
            }
        }

        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const meetingStatusName = statusTypeOptions.find(x => x.id === data.meetingStatusType)?.name ?? "";
        const payload = {
            leadId, ...data,
            locationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
            LocationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
            meetingStatusName,
            meetingDuration: Number(data.meetingDuration),
            startDateTime: !data.isAllDay && data.startDateTime ? new Date(`${data.meetingDate}T${data.startDateTime}`).toISOString() : null,
            endDateTime: !data.isAllDay && data.endDateTime
                ? new Date(`${data.meetingDate}T${data.endDateTime}`).toISOString()
                : null,
            extraEmails: finalEmails,
            timeZoneId: timeZoneId,
            meetingLinkType: data.meetingLinkType,
            meeting_Link: data.meetingLinkType === "googleLink" ? "googleLink" : data.meetingLinkType === "locationVisit" ? "" : data.meeting_Link,
            IncludeSignature: includeSignature,
            IncludeDisclaimer: includeDisclaimer,
            SignatureType: signatureType,
        };

        const submitRequest = async (showSuccessMsg: boolean) => {
            const msg = showSuccessMsg ? (editId ? "Meeting Updated Successfully" : "Meeting Added Successfully") : undefined;
            return editId
                ? await fetchApi(`LeadRequirement/LeadMeeting/${editId}`, "PUT", payload, null, msg)
                : await fetchApi("LeadRequirement/LeadMeeting", "POST", payload, null, msg);
        };

        const handleSuccessFlow = () => {
            setShowForm(false);
            setEmailList(pocEmail ? [pocEmail] : []);
            setEmailInput("");
            setIncludeSignature(false);
            setIncludeDisclaimer(false);
            setSignatureType("Short");
            reset();
            loadMeetings();
            onMeetingSaved?.(leadId);
            refreshHistory("Lead", leadId);
        };

        const res = await submitRequest(false);

        if (res && res.requiresAuth && res.authUrl) {
            const width = 500;
            const height = 650;
            const left = window.screen.width / 2 - width / 2;
            const top = window.screen.height / 2 - height / 2;

            const popup = window.open(
                res.authUrl,
                "google-oauth-popup",
                `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
            );

            if (popup) {
                const handleOAuthMessage = async (event: MessageEvent) => {
                    if (event.data === "google-connected") {
                        window.removeEventListener("message", handleOAuthMessage);

                        addToast({
                            message: "Google account connected! Saving meeting...",
                            status: "info",
                        });

                        const retryRes = await submitRequest(true);
                        if (retryRes && !retryRes.requiresAuth) {
                            handleSuccessFlow();
                        }
                    }
                };

                window.addEventListener("message", handleOAuthMessage);
            } else {
                addToast({
                    message: "Popup blocked. Please allow popups to connect Google Calendar.",
                    status: "warning",
                });
            }
            return;
        }

        if (res) {
            addToast({
                message: editId ? "Meeting Updated Successfully" : "Meeting Added Successfully",
                status: "success",
            });
            handleSuccessFlow();
        }
    };



    const handleEdit = (row: IMeeting) => {
        setOriginalStatus(row.meetingStatusType);
        setIsReadOnlyMeeting(row.meetingStatusType === StatusType.Done || row.meetingStatusType === StatusType.Cancelled);
        setEditId(row.id!);
        setEmailList(row.extraEmails ?? []);
        reset({
            ...row,
            locationId: row.locationId ?? (row as any).LocationId ?? undefined,
            meetingDate: row.isAllDay
                ? row.meetingDate?.split("T")[0] || ""
                : row.startDateTime
                    ? new Date(`${row.startDateTime}Z`)
                        .toLocaleDateString("en-CA")
                    : "",
            startDateTime: row.isAllDay
                ? ""
                : row.startDateTime
                    ? new Date(`${row.startDateTime}Z`).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    })
                    : "",
            endDateTime: row.isAllDay ? "" : row.endDateTime ? new Date(`${row.endDateTime}Z`).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
                : "",
            remarks: row.remarks || "",
            meetingLinkType: row.meetingLinkType ?? (row.locationId ? "locationVisit" : "googleLink"),
            meeting_Link: row.meetingLinkType === "usingLink" ? row.meeting_Link || "" : "",
        });
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setEditId(null);
        setOriginalStatus(null);
        setIsReadOnlyMeeting(false);
        setEmailList(pocEmail ? [pocEmail] : []); setEmailInput(""); setEmailError("");
        setIncludeSignature(false);
        setIncludeDisclaimer(false);
        setSignatureType("Short");
        reset(defaultValues);
    };

    useEffect(() => {
        if (isAllDay) {
            setValue("startDateTime", "");
            setValue("endDateTime", "");
            setValue("meetingDuration", 0);
            if (selectedMeetingLinkType === "googleLink") {
                setValue("meetingLinkType", "usingLink");
            }
        }
    }, [isAllDay, setValue, selectedMeetingLinkType]);

    const meetingDuration = watch("meetingDuration");

    useEffect(() => {
        if (startTime && meetingDuration) {
            const start = new Date(`2000-01-01T${startTime}`);

            start.setMinutes(
                start.getMinutes() + Number(meetingDuration)
            );
            const hh = String(start.getHours()).padStart(2, "0");
            const mm = String(start.getMinutes()).padStart(2, "0");

            setValue("endDateTime", `${hh}:${mm}`);
        }
    }, [startTime, meetingDuration]);

    const isMeetingExpired = (row: IMeeting) => {
        // All-day meeting
        if (row.isAllDay) {
            if (!row.meetingDate) return false;
            const meetingDate = row.meetingDate.split("T")[0]; // YYYY-MM-DD
            const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
            return meetingDate < today;
        }
        // Timed meeting
        if (!row.endDateTime) return false;
        const endDateLocal = new Date(`${row.endDateTime}Z`);
        const nowLocal = new Date();
        return endDateLocal < nowLocal;
    };

    const isMeetingToday = (row: IMeeting) => {
        if (isMeetingExpired(row)) return false;
        if (isMeetingApproaching(row)) return false;
        const today = new Date();
        if (row.isAllDay) {
            const meetingDate = row.meetingDate.split("T")[0]; // "2026-07-11"
            const today = new Date().toLocaleDateString("en-CA"); // "2026-07-11"
            return (meetingDate === today && row.meetingStatusType === StatusType.Scheduled);
        }
        // Normal meeting
        if (!row.startDateTime) return false;
        const meetingDate = new Date(`${row.startDateTime}Z`);
        return (
            meetingDate.getFullYear() === today.getFullYear() &&
            meetingDate.getMonth() === today.getMonth() &&
            meetingDate.getDate() === today.getDate() &&
            row.meetingStatusType === StatusType.Scheduled
        );
    };


    const isMeetingApproaching = (row: IMeeting) => {
        if (row.meetingStatusType !== StatusType.Scheduled || !row.startDateTime || !row.endDateTime) {
            return false;
        }
        const now = new Date();
        const meetingStart = new Date(`${row.startDateTime}Z`);
        const meetingEnd = new Date(`${row.endDateTime}Z`);
        const diffMinutes = (meetingStart.getTime() - now.getTime()) / (1000 * 60);
        return diffMinutes <= 15 && now <= meetingEnd;
    };

    return (
        <MyDiv>
            <Box>
                <Flex justify="space-between" align="center" mb={4}>
                    <Text className="font-poppins text_lg text_semibold">
                        Meetings
                    </Text>
                    <Flex gap={3} align="center">
                        <Select size="sm" w="150px" bg="white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            {statusFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>
                        <Select size="sm" w="150px" bg="white" value={meetingFilter} onChange={(e) => setMeetingFilter(e.target.value)}>
                            {meetingFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>
                        <Button size="sm" className="font-poppins text_medium text_sm" bg="#4048D6" color="white" _hover={{ bg: "#353CC4" }} leftIcon={<Image src={AddIcon} boxSize="14px" objectFit="contain" />} onClick={() => { setEmailList(pocEmail ? [pocEmail] : []); setShowForm(true); }} >
                            New Meeting
                        </Button>
                    </Flex>
                </Flex>
                {viewType === "table" ? (
                    <Box>
                        {filterLoading ? (
                            <Flex justify="center" py={10}>
                                <Spinner thickness="2px" speed="0.65s" color="#0052CC" size="lg" />
                            </Flex>
                        ) : (
                            <CustomTable value={meetings} showEmptyMessage={false} pagination={false}
                                rowClassName={(row) => {
                                    if (isMeetingExpired(row)) {
                                        return "expired-row";
                                    }
                                    if (isMeetingApproaching(row)) {
                                        return "blink-approaching";
                                    }
                                    if (isMeetingToday(row)) {
                                        return "blink-today";
                                    }
                                    return "";
                                }}  >
                                <Column header="S.No" body={(_, index) => index + 1} />
                                <Column header="Title" field="meetingTitle" />
                                <Column header="Meeting Link" body={(row: IMeeting) => {
                                    if (row.meetingLinkType === "locationVisit" || (!row.meeting_Link && (row.locationName || row.locationMaster?.locationName))) {
                                        return (
                                            <Text color="#101828" fontWeight="500">
                                                📍 {row.locationName || row.locationMaster?.locationName || locations.find(l => l.id === row.locationId)?.name || "Location Visit"}
                                            </Text>
                                        );
                                    }
                                    const hasLink = isValidUrl(row.meeting_Link); if (!hasLink) return <Text color="gray.400">{row.meetingLinkType === "googleLink" ? "Google Meet" : "-"}</Text>; const active = isMeetingLinkActive(row);
                                    return (
                                        <Flex align="center" gap={2}>
                                            {active ? (
                                                <Box as="a" href={row.meeting_Link} target="_blank" rel="noopener noreferrer" color="#4048D6" fontWeight="600" _hover={{ textDecoration: "underline" }}>
                                                    {row.meetingLinkType === "googleLink" ? "Google Meet" : "Custom Link"}
                                                </Box>
                                            ) : (
                                                <Text color="gray.400" fontWeight="500" cursor="not-allowed" title="Link will be active during the scheduled meeting time">
                                                    {row.meetingLinkType === "googleLink" ? "Google Meet (Inactive)" : "Custom Link (Inactive)"}
                                                </Text>
                                            )}
                                            <IconButton aria-label="Copy link" icon={<CopyIcon />} size="xs" variant="ghost" color={active ? "#667085" : "gray.300"} isDisabled={!active} onClick={(e) => { e.stopPropagation(); if (active) handleCopyLink(row.meeting_Link!); }} _hover={active ? { color: "#4048D6", bg: "#F9FAFB" } : undefined} />
                                        </Flex>
                                    );
                                }}
                                />
                                <Column header="Meeting Date" body={(row: IMeeting) => {
                                    const expired = isMeetingExpired(row);
                                    return (
                                        <Box bg={expired ? "#FEE2E2" : "transparent"} px={2} py={1} borderRadius="6px" width="100%">
                                            {row.isAllDay
                                                ? formatMeetingDate(row.meetingDate)
                                                : showDate(row.startDateTime)}
                                        </Box>
                                    );
                                }} />
                                <Column header="Start Time" body={(row: any) => showTime(row.startDateTime)} />
                                <Column header="End Time" body={(row: any) => showTime(row.endDateTime)} />
                                <Column header="Duration" body={(row: any) => `${row.meetingDuration} Minutes`} />
                                <Column header="Created At" body={(row: any) => row.createdAt
                                    ? `${showDate(row.createdAt)} ${showTime(row.createdAt)}`
                                    : "-"
                                } />
                                <Column header="Created By" body={(row: any) => row.createdByName || "-"} />
                                <Column header="Location" body={(row: any) => row.locationName || row.locationMaster?.locationName || locations.find(l => l.id === row.locationId)?.name || "-"} />
                                <Column header="Status" field="meetingStatusName" />
                                <Column header="Action" body={(row: any) => (
                                    <EditIcon cursor="pointer" color="#4048D6" onClick={() => handleEdit(row)} />
                                )} />
                            </CustomTable>
                        )}
                    </Box>
                ) : (
                    <>
                        {filterLoading ? (
                            <Flex justify="center" align="center" height="300px" >
                                <Spinner thickness="2px" speed="0.65s" color="#0052CC" size="lg" />
                            </Flex>
                        ) : meetings.length === 0 ? (
                            <Flex direction="column" justify="center" align="center" height="300px" >
                                <Text color="gray.500">No Meetings Found</Text>
                            </Flex>
                        ) : (
                            <Grid gap={4}>
                                {meetings.map((meeting) => (
                                    <Box key={meeting.id} bg="white" border="1px solid #E4E7EC" borderRadius="16px" p={5}>
                                        <Grid templateColumns="1fr auto" alignItems="start" mb={4}>
                                            <Box>
                                                <Text fontSize="lg" fontWeight="600"> {meeting.meetingTitle}</Text>
                                                <Text fontSize="sm" color="gray.500">{meeting.meetingStatusName}</Text>
                                            </Box>
                                            <EditIcon cursor="pointer" color="#4048D6" onClick={() => handleEdit(meeting)} />
                                        </Grid>
                                        <Grid templateColumns={{ base: "repeat(2,1fr)", md: "repeat(6,1fr)", }} gap={6}>
                                            <Box>
                                                <Text color="#667085" fontSize="13px">Meeting Date</Text>
                                                <Text fontWeight="500">{showDate(meeting.meetingDate)}</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="13px"  >Start Time</Text>
                                                <Text fontWeight="500">{showTime(meeting.startDateTime)}</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="13px">End Time</Text>
                                                <Text fontWeight="500">{showTime(meeting.endDateTime)}</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="13px">Duration</Text>
                                                <Text fontWeight="500">{meeting.meetingDuration} Min</Text>
                                            </Box>
                                            <Box>
                                                <Text color="#667085" fontSize="13px">Created By</Text>
                                                <Text fontWeight="500">{meeting.createdByName}</Text>
                                            </Box>
                                            {(meeting.locationName || meeting.locationMaster?.locationName || meeting.locationId) && (
                                                <Box>
                                                    <Text color="#667085" fontSize="13px">Location</Text>
                                                    <Text fontWeight="500">{meeting.locationName || meeting.locationMaster?.locationName || locations.find(l => l.id === meeting.locationId)?.name || "-"}</Text>
                                                </Box>
                                            )}
                                            <Box>
                                                <Text color="#667085" fontSize="13px">Meeting Link</Text>
                                                {meeting.meetingLinkType === "locationVisit" ? (
                                                    <Text fontWeight="500" color="#101828">
                                                        📍 {meeting.locationName || meeting.locationMaster?.locationName || locations.find(l => l.id === meeting.locationId)?.name || "Location Visit"}
                                                    </Text>
                                                ) : (
                                                    <Flex align="center" gap={1}>
                                                        {isValidUrl(meeting.meeting_Link) ? (
                                                            isMeetingLinkActive(meeting) ? (
                                                                <a href={meeting.meeting_Link} target="_blank" rel="noopener noreferrer" style={{ color: "#4048D6", textDecoration: "underline", fontWeight: "500" }}>
                                                                    {meeting.meetingLinkType === "googleLink" ? "Google Meet" : "Custom Link"}
                                                                </a>
                                                            ) : (
                                                                <Text color="gray.400" fontWeight="500" cursor="not-allowed" title="Link will be active during the scheduled meeting time">
                                                                    {meeting.meetingLinkType === "googleLink" ? "Google Meet (Inactive)" : "Custom Link (Inactive)"}
                                                                </Text>
                                                            )
                                                        ) : (
                                                            <Text fontWeight="500">
                                                                {meeting.meetingLinkType === "googleLink" ? "Google Meet" : "No Link"}
                                                            </Text>
                                                        )}
                                                        {isValidUrl(meeting.meeting_Link) && (
                                                            <IconButton aria-label="Copy link" icon={<CopyIcon />} size="xs" variant="ghost" color={isMeetingLinkActive(meeting) ? "#667085" : "gray.300"} isDisabled={!isMeetingLinkActive(meeting)}
                                                                onClick={(e) => { e.stopPropagation(); if (isMeetingLinkActive(meeting)) handleCopyLink(meeting.meeting_Link!); }} _hover={isMeetingLinkActive(meeting) ? { color: "#4048D6", bg: "#F9FAFB" } : undefined} />
                                                        )}
                                                    </Flex>
                                                )}
                                            </Box>
                                        </Grid>

                                        {meeting.description && (
                                            <Box mt={4} pt={4} borderTop="1px solid #EAECF0">
                                                <Text color="#667085" fontSize="13px" mb={1}>Description</Text>
                                                <Text fontSize="14px" color="#101828">{meeting.description}
                                                </Text>
                                            </Box>
                                        )
                                        }
                                    </Box>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
                <Modal isOpen={showForm} onClose={closeModal} size="2xl">
                    <ModalOverlay />
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <ModalContent>
                            <ModalHeader>
                                {editId ? "Edit Meeting" : "New Meeting"}
                            </ModalHeader>
                            <ModalCloseButton />
                            <ModalBody maxH="400px" overflow="scroll">
                                <Grid templateColumns="repeat(2,1fr)" gap={4}>
                                    <GridItem colSpan={2}>
                                        <Controller name="isAllDay" control={control} render={({ field }) => (<FormControl display="flex" alignItems="center">
                                            <FormLabel mb="0">All Day</FormLabel>
                                            <Switch isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} isDisabled={isReadOnlyMeeting} colorScheme="green" />     </FormControl>)}
                                        />
                                    </GridItem>
                                    {/* Email Invite Input */}
                                    <GridItem colSpan={2}>
                                        <FormControl isInvalid={!!emailError}>
                                            <FormLabel className="font-poppins text_medium text_md">
                                                Invite via Email
                                            </FormLabel>

                                            {/* Email chips */}
                                            {emailList.length > 0 && (
                                                <Flex flexWrap="wrap" gap={2} mb={2} p={2} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                                                    {emailList.map(email => (
                                                        <Flex key={email} align="center" gap={1} bg="#EEF0FD" color="#4048D6" borderRadius="full" px={3} py={1} fontSize="sm" fontWeight="500">  <Text>{email}</Text>  <Text cursor="pointer" fontWeight="bold" ml={1} onClick={() => removeEmail(email)} _hover={{ color: "red.500" }}>×</Text></Flex>
                                                    ))}
                                                </Flex>
                                            )}
                                            <Input placeholder="Type email and press Enter or , to add" value={emailInput} onChange={e => { setEmailInput(e.target.value); setEmailError(""); }} onKeyDown={handleEmailKeyDown} onBlur={handleEmailBlur} bg="white" isDisabled={isReadOnlyMeeting} />
                                            {emailError && <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>}
                                            <Text color="gray.400" fontSize="xs" mt={1}>
                                                Press <strong>Enter</strong> or <strong>,</strong> to add multiple emails
                                            </Text>
                                        </FormControl>
                                    </GridItem>
                                    <GridItem colSpan={2}>
                                        <FormInput name="meetingTitle" control={control} errors={errors} label="Title" isRequired disable={isReadOnlyMeeting} />
                                    </GridItem>
                                    <GridItem>
                                        <FormInput isRequired name="meetingDate" control={control} errors={errors} label="Date" type="date" min={today} disable={isReadOnlyMeeting} />
                                    </GridItem>
                                    <GridItem>
                                        <Dropdown control={control} name="meetingDuration" label="Duration" options={meetingDurationOptions} valueKey="id" labelKey="name" errors={errors} disable={isReadOnlyMeeting} />
                                    </GridItem>
                                    <FormInput name="startDateTime" control={control} errors={errors} label="Start Time" type="time" disable={isAllDay || isReadOnlyMeeting} />
                                    <FormInput name="endDateTime" control={control} label="End Time" type="time" disable={true} />
                                    <GridItem>
                                        <Dropdown
                                            control={control}
                                            name="meetingStatusType"
                                            label="Status"
                                            options={getStatusOptions(originalStatus ?? undefined)}
                                            valueKey="id"
                                            labelKey="name"
                                            errors={errors}
                                            disable={
                                                originalStatus === StatusType.Done || originalStatus === StatusType.Cancelled
                                            }
                                        />
                                    </GridItem>

                                    {editId && (
                                        <GridItem colSpan={2}>
                                            <FormControl >
                                                <FormLabel className="font_poppins text_md text_medium"> Remarks</FormLabel>
                                                <Textarea placeholder="Add Remarks" bg={'white'} className=" font_poppins text_regular text_sm"  {...register(`remarks`)} isDisabled={isReadOnlyMeeting} />
                                            </FormControl>
                                        </GridItem>
                                    )}
                                    <GridItem>
                                        <Dropdown control={control} name="meetingLinkType" label="Meeting Type" options={meetingLinkTypeOptions} valueKey="id" labelKey="name" errors={errors} disable={isReadOnlyMeeting} />
                                    </GridItem>

                                    {selectedMeetingLinkType === "usingLink" && (
                                        <GridItem>
                                            <FormInput name="meeting_Link" control={control} errors={errors} label="Meeting Link" placeholder="Enter meeting URL" disable={isReadOnlyMeeting} />
                                        </GridItem>
                                    )}

                                    {selectedMeetingLinkType === "locationVisit" && (
                                        <GridItem>
                                            <Dropdown
                                                isRequired
                                                control={control}
                                                name="locationId"
                                                label="Location"
                                                placeholder="Select Location"
                                                options={locations}
                                                valueKey="id"
                                                labelKey="name"
                                                errors={errors}
                                                disable={isReadOnlyMeeting}
                                            />
                                        </GridItem>
                                    )}

                                    {editId && watch("meeting_Link") && isValidUrl(watch("meeting_Link")) && (() => {
                                        const currentMeeting = meetings.find(m => m.id === editId);
                                        const active = currentMeeting ? isMeetingLinkActive(currentMeeting) : false;
                                        return (
                                            <GridItem colSpan={2}>
                                                <FormControl>
                                                    <FormLabel className="font-poppins text_medium text_md">
                                                        Joining Link {!active && <Text as="span" color="gray.400" fontSize="sm">(Inactive until meeting starts)</Text>}
                                                    </FormLabel>
                                                    <Flex align="center" gap={3} p={3} bg={active ? "#EEF0FD" : "gray.50"} borderRadius="8px" border="1px solid" borderColor={active ? "#C7D7FE" : "gray.200"}>
                                                        {active ? (
                                                            <Text as="a" href={watch("meeting_Link")} target="_blank" rel="noopener noreferrer" color="#4048D6" fontWeight="600" textDecoration="underline" isTruncated flex="1">
                                                                {watch("meeting_Link")}
                                                            </Text>
                                                        ) : (
                                                            <Text color="gray.400" fontWeight="500" cursor="not-allowed" isTruncated flex="1" title="Link will be active during the scheduled meeting time">
                                                                {watch("meeting_Link")} (Inactive)
                                                            </Text>
                                                        )}
                                                        <Button size="sm" leftIcon={<CopyIcon />} onClick={() => { if (active) handleCopyLink(watch("meeting_Link")!); }} isDisabled={!active} bg={active ? "#4048D6" : "gray.200"} color={active ? "white" : "gray.400"} _hover={active ? { bg: "#353CC4" } : undefined}>
                                                            Copy Link
                                                        </Button>
                                                    </Flex>
                                                </FormControl>
                                            </GridItem>
                                        );
                                    })()}
                                    <GridItem colSpan={2}>
                                        <FormControl isInvalid={!!errors.description} isRequired>
                                            <FormLabel className="font-poppins text_medium text_md" >
                                                Description
                                            </FormLabel>
                                            <Textarea placeholder="Add Description" bg="white" {...register("description")} isDisabled={isReadOnlyMeeting} />
                                            {errors.description && (
                                                <Text color="red.500" fontSize="sm" mt={1}>{errors.description.message}
                                                </Text>
                                            )}
                                        </FormControl>
                                    </GridItem>
                                    <GridItem colSpan={2}>
                                        <FormControl>
                                            <FormLabel>Email Options</FormLabel>

                                            <Flex direction="column" gap={4}>
                                                <Box>
                                                    <Checkbox isChecked={includeSignature}
                                                        onChange={(e) => setIncludeSignature(e.target.checked)}
                                                        isDisabled={isReadOnlyMeeting}
                                                    >
                                                        Show Signature
                                                    </Checkbox>
                                                    {includeSignature && (
                                                        <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                            <RadioGroup onChange={(val) => setSignatureType(val as "Short" | "Full")} value={signatureType}>
                                                                <Flex gap={4} mb={2}>
                                                                    <Radio value="Short" colorScheme="blue" isDisabled={isReadOnlyMeeting}>Short Signature</Radio>
                                                                    <Radio value="Full" colorScheme="blue" isDisabled={isReadOnlyMeeting}>Full Signature</Radio>
                                                                </Flex>
                                                            </RadioGroup>
                                                            {loadingSignature ? (
                                                                <Flex align="center" gap={2} mt={2}>
                                                                    <Spinner size="xs" color="#4048D6" />
                                                                    <Text fontSize="xs" color="gray.500">Loading signature...</Text>
                                                                </Flex>
                                                            ) : (
                                                                <Box 
                                                                    mt={2} 
                                                                    p={3} 
                                                                    bg="gray.50" 
                                                                    borderRadius="md" 
                                                                    border="1px solid" 
                                                                    borderColor="gray.200" 
                                                                    maxH="150px" 
                                                                    overflowY="auto"
                                                                    fontSize="sm"
                                                                >
                                                                    {signatureType === "Short" ? (
                                                                        userSignature?.shortSignature ? (
                                                                            <Box dangerouslySetInnerHTML={{ __html: userSignature.shortSignature }} />
                                                                        ) : (
                                                                            <Text color="gray.400" fontStyle="italic">No short signature configured.</Text>
                                                                        )
                                                                    ) : (
                                                                        userSignature?.fullSignature ? (
                                                                            <Box dangerouslySetInnerHTML={{ __html: userSignature.fullSignature }} />
                                                                        ) : (
                                                                            <Text color="gray.400" fontStyle="italic">No full signature configured.</Text>
                                                                        )
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>

                                                <Box>
                                                    <Checkbox
                                                        isChecked={includeDisclaimer}
                                                        onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                                                        isDisabled={isReadOnlyMeeting}
                                                    >
                                                        Show Disclaimer
                                                    </Checkbox>
                                                    {includeDisclaimer && (
                                                        <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                            {loadingDisclaimer ? (
                                                                <Flex align="center" gap={2}>
                                                                    <Spinner size="xs" color="#4048D6" />
                                                                    <Text fontSize="xs" color="gray.500">Loading disclaimer...</Text>
                                                                </Flex>
                                                            ) : (
                                                                <Box 
                                                                    p={3} 
                                                                    bg="gray.50" 
                                                                    borderRadius="md" 
                                                                    border="1px solid" 
                                                                    borderColor="gray.200" 
                                                                    maxH="150px" 
                                                                    overflowY="auto"
                                                                    fontSize="sm"
                                                                >
                                                                    {websiteDisclaimer ? (
                                                                        <Box dangerouslySetInnerHTML={{ __html: websiteDisclaimer }} />
                                                                    ) : (
                                                                        <Text color="gray.400" fontStyle="italic">No disclaimer configured.</Text>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Flex>
                                        </FormControl>
                                    </GridItem>
                                </Grid>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="outline" mr={3} onClick={closeModal}>Cancel</Button>
                                <Button bg="#4048D6" color="white" type="submit" isLoading={loading} isDisabled={isReadOnlyMeeting}>{editId ? "Update" : "Save"}</Button>
                            </ModalFooter>
                        </ModalContent>
                    </form>
                </Modal>
            </Box>
        </MyDiv >
    );
};

export default MeetingFormTab;